import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { createCheckoutSession, cancelSubscription } from '../services/subscription';
import SEO, { SEOConfigs } from '../components/SEO';
import {
  Typography,
  Box,
  Paper,
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { format, isAfter } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

const Subscription: React.FC = () => {
  const { subscription, updateSubscription } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      setError(null);

      const checkout = await createCheckoutSession();

      // Redirect to Abacate Pay checkout page
      window.location.href = checkout.url;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Falha ao iniciar o checkout. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      await cancelSubscription();

      // Update subscription state
      updateSubscription({
        ...subscription!,
        status: 'CANCELLED'
      });

      setCancelDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Falha ao cancelar assinatura. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderSubscriptionStatus = () => {
    if (!subscription) return null;

    switch (subscription.status) {
      case 'ACTIVE_TRIAL':
        const trialEndDate = new Date(subscription.trialEndDate || '');
        const isTrialActive = isAfter(trialEndDate, new Date());

        return (
          <Box sx={{ mb: 4 }}>
            <Alert severity="info">
              <Typography variant="h6">
                Você está no período de Trial
              </Typography>
              {isTrialActive ? (
                <Typography variant="body1">
                  Seu período de trial termina em {format(trialEndDate, 'dd/MM/yyyy', { locale: ptBR })}
                </Typography>
              ) : (
                <Typography variant="body1">
                  Seu período de trial expirou. Assine agora para continuar com acesso premium.
                </Typography>
              )}
            </Alert>
          </Box>
        );

      case 'ACTIVE_PAID':
        const renewDate = new Date(subscription.subscriptionEndDate || '');

        return (
          <Box sx={{ mb: 4 }}>
            <Alert severity="success">
              <Typography variant="h6">
                Assinatura Ativa
              </Typography>
              <Typography variant="body1">
                Sua próxima cobrança será em {format(renewDate, 'dd/MM/yyyy', { locale: ptBR })}
              </Typography>
              <Button
                color="error"
                variant="outlined"
                onClick={() => setCancelDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Cancelar Assinatura
              </Button>
            </Alert>
          </Box>
        );

      case 'EXPIRED_TRIAL':
        return (
          <Box sx={{ mb: 4 }}>
            <Alert severity="warning">
              <Typography variant="h6">
                Seu período de trial expirou
              </Typography>
              <Typography variant="body1">
                Assine agora para continuar com acesso premium.
              </Typography>
            </Alert>
          </Box>
        );

      case 'CANCELLED':
        return (
          <Box sx={{ mb: 4 }}>
            <Alert severity="error">
              <Typography variant="h6">
                Assinatura Cancelada
              </Typography>
              <Typography variant="body1">
                Você não tem mais acesso aos recursos premium.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO {...SEOConfigs.register} />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Assinatura
          </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {renderSubscriptionStatus()}

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Planos Disponíveis
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card raised>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="h5" gutterBottom>
                    Plano Mensal
                  </Typography>
                  <Chip label="Recomendado" color="primary" />
                </Box>
                <Typography variant="h4" color="primary" gutterBottom>
                  R$ 29,90
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    /mês
                  </Typography>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Acesso completo a todas as funcionalidades premium
                </Typography>
                <ul>
                  <li>Recurso premium 1</li>
                  <li>Recurso premium 2</li>
                  <li>Recurso premium 3</li>
                  <li>Suporte prioritário</li>
                </ul>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading || subscription?.status === 'ACTIVE_PAID'}
                  onClick={handleSubscribe}
                >
                  {loading ? <CircularProgress size={24} /> :
                    subscription?.status === 'ACTIVE_PAID' ? 'Assinatura Ativa' : 'Assinar Agora'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Cancel Subscription Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      >
        <DialogTitle>Cancelar Assinatura?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja cancelar sua assinatura? Você perderá acesso aos recursos premium imediatamente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Não, Manter</Button>
          <Button
            onClick={handleCancelSubscription}
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Sim, Cancelar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default Subscription;
